import { useRef, useEffect, useState, useCallback } from 'react';
import { Channel, ChannelStatus, ChannelLayout } from 'core/models/channel';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { MediaDeviceRepository } from 'core/repositories/MediaDeviceRepository';
import { useMediaDeviceRepository } from './useMediaDeviceRepository';

function startStatusMonitoringIfNeeded(channel: Channel, body: () => Promise<void>) {
    return new Promise<void>((resolve, reject) => {
        try {
            let first = true;
            const isFirst = () => first;
            const listener = () => {
                if (!isFirst()) return;
                first = false;

                (async () => {
                    await body();
                    channel.removeStatusListener(listener);
                    resolve();
                })();
            };
            channel.addStatusListener(listener);
        } catch (e) {
            reject(e);
        }
    });
}

async function waitForPublishingState(channel: Channel, publishing: boolean) {
    return new Promise<void>((resolve, reject) => {
        try {
            if (channel.currentStatus?.publishing === publishing) {
                resolve();
                return;
            }

            const listener = (status: ChannelStatus) => {
                if (status.publishing === publishing) {
                    channel.removeStatusListener(listener);
                    resolve();
                }
            };
            channel.addStatusListener(listener);
        }
        catch (e) {
            reject(e);
        }
    });
}

async function reloadStream(mediaDeviceRepository: MediaDeviceRepository, projectId: Project.Id, environmentId: Environment.Id, channel: Channel) {
    await startStatusMonitoringIfNeeded(channel, async () => {
        if (channel.currentStatus?.publishing !== true) {
            console.info(`${channel.mediaDeviceId} is not active, so we do not restart it this time`);
            return;
        }

        await mediaDeviceRepository.restartPublish(channel.mediaDeviceId, projectId, environmentId);

        await waitForPublishingState(channel, true);
    });
}

async function reloadStreams(mediaDeviceRepository: MediaDeviceRepository, projectId: Project.Id, environmentId: Environment.Id, channels: Channel[]) {
    await Promise.all(channels.map(channel => reloadStream(mediaDeviceRepository, projectId, environmentId, channel)));
}

export function useReloadingStreams(channelLayout: ChannelLayout | undefined, projectId: Project.Id, environmentId: Environment.Id) {
    const mediaDeviceRepository = useMediaDeviceRepository();

    const targets = useRef<Channel[]>([]);
    useEffect(() => {
        if (channelLayout) {
            targets.current = [...channelLayout.cameras.values()];
        } else {
            targets.current = [];
        }
    }, [channelLayout]);

    const [, setReloadable] = useState(true);
    const [isReloading, setReloading] = useState(false);
    const onReloadClick = useCallback(() => {
        setReloadable(reloadable => {
            if (reloadable) {
                (async () => {
                    try {
                        await reloadStreams(mediaDeviceRepository, projectId, environmentId, targets.current);
                        setReloading(false);
                        setReloadable(true);
                    } catch (e) {
                        console.error(e);
                    }
                })();
                setReloading(true);
                return false;
            }
            return false;
        });
    }, [environmentId, mediaDeviceRepository, projectId]);

    return [isReloading, onReloadClick] as const;
}
